/**
 * router.tsx — rutas de la app. /login pública; el resto protegido por AuthGuard.
 * "/" no tiene página propia: redirige según rol (admin → /admin, técnico → /tecnico).
 *
 * TOG-01: RootRedirect honors the view-as overlay (downgrade-only) so an admin
 * viewing as technician lands on the technician panel. AdminGuard continues to
 * enforce the real JWT role for route protection — it is not affected by viewAs.
 *
 * Admin routes use a shared AdminLayout (left rail + <Outlet />) so each section
 * is a full-page route instead of a state-driven tab switch.
 */
import { Navigate, Route, Routes } from "react-router-dom";
import { LoginPage, AuthGuard, AdminGuard, useAuth, useViewAs } from "@/features/auth/index.js";
import { TechnicianPanel } from "@/features/technician-panel/index.js";
import {
  AdminLayout,
  AdminDashboard,
  UsuariosPage,
  ClientesPage,
  MarcaPage,
  AdminPerfilPage,
  AdminManualPage,
} from "@/features/admin-panel/index.js";
import { NuevoInforme, InformeEditor, InformeDetalle } from "@/features/reports/index.js";
import { PerfilPage, ManualPage } from "@/features/account/index.js";
import { resolveViewRole, homePathForRole } from "@/features/auth/view-role.js";

/** Landing por rol: respects view-as overlay (TOG-01) for downgrade routing. */
function RootRedirect() {
  const { user } = useAuth();
  const { viewAs } = useViewAs();
  const effectiveRole = resolveViewRole(user?.rol ?? "technician", viewAs);
  return <Navigate to={homePathForRole(effectiveRole)} replace />;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<AuthGuard />}>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/tecnico" element={<TechnicianPanel />} />
        <Route path="/informe/nuevo" element={<NuevoInforme />} />
        <Route path="/informe/:id" element={<InformeEditor />} />
        <Route path="/informe/:id/detalle" element={<InformeDetalle />} />
        <Route path="/perfil" element={<PerfilPage />} />
        <Route path="/manual" element={<ManualPage />} />
      </Route>
      <Route element={<AdminGuard />}>
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/usuarios" element={<UsuariosPage />} />
          <Route path="/admin/clientes" element={<ClientesPage />} />
          <Route path="/admin/marca" element={<MarcaPage />} />
          <Route path="/admin/perfil" element={<AdminPerfilPage />} />
          <Route path="/admin/manual" element={<AdminManualPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
